import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";

import { aggridHeaderClass, blueButtonClass } from "src/components/common/common";
import { InputForm } from "src/components/common/form/input/InputForm";
import { KioskWorkStandardButtonForm } from "src/components/common/form/KioskWorkStandardButtonForm";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useDescText } from "src/hooks/useDescText";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { DESC_BTN, DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CreateCellDoubleClickHandler,
	createGridDropDownCommitAfter,
	CustomCellClickAction,
	CustomCellDoubleClickAction,
	GridCellConfig,
	useCellChangeHandler,
	useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { showWarning } from "src/utils/toast/toastUtil";

import { KSK2140_R01InspectDto } from "./Dto_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";
import { getKSK2140_R01InspectKey, getLotListByInspectFinish } from "./Util_KSK2140_R01";

const COLUMN_STATE_KEY = "KSK2140_R01-inspect-grid-column-state";

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: [""],
	insertedDisabledCells: [""],
	commonDisabledCells: ["badCase"],
	pointerCells: [""],
	insertedPointerCells: ["badCase", "badQty"],
	commonPointerCells: [""],
	alignLeftCells: [""],
};

const isInspectRowEditable = (data?: KSK2140_R01InspectDto | null) =>
	data?.inserted === true;

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2140_R01InspectDto> | null>;
	defaultKSK2140_R01InspectRow: KSK2140_R01InspectDto;
	handleWorkStandardClick: (sheetNo: 1 | 2) => void;
}

export const InspectTable_KSK2140_R01 = ({
	gridRef,
	defaultKSK2140_R01InspectRow,
	handleWorkStandardClick,
}: Props) => {
	const {
		KSK2140_R01InspectMap,
		setKSK2140_R01InspectList,
		selectedKSK2140_R01List,
		selectedKSK2140_R01InspectList,
		addSelectedKSK2140_R01InspectList,
		removeSelectedKSK2140_R01InspectList,
		initSelectedKSK2140_R01InspectList,
		deletedKSK2140_R01InspectList,
		insertKSK2140_R01InspectList,
		removeKSK2140_R01InspectList,
		updateKSK2140_R01InspectList,
		KSK2140_R01DetailList,
		updateKSK2140_R01DetailList,
		setKSK2140_R01LotList,
		inspectColumnState,
		setInspectColumnState,
		originalKSK2140_R01InspectList,
		activeInspectKey,
		inspectQtyInput,
		setInspectQtyInput,
	} = useKSK2140_R01Store();
	const { faultyValueList } = useCommonCodeStore();
	const { renderDescText, getFormTitleWidth } = useDescText();
	const countTemplate = renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건");
	const noDataOverlay = `<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`;
	const [clickable, setClickable] = useState(true);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const inspectRows = useMemo(
		() => KSK2140_R01InspectMap.get(activeInspectKey) || [],
		[KSK2140_R01InspectMap, activeInspectKey],
	);

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		badCaseNm: renderDescText(DESC_FLD.DEFECT_REASON, "불량사유"),
		badQty: renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"),
	}), [renderDescText]);
	const formTitleWidth = useMemo(() => getFormTitleWidth([
		renderDescText(DESC_FLD.INSPECTION_QTY, "검사량"),
		renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"),
	], 100, 230), [getFormTitleWidth, renderDescText]);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: inspectRows,
		defaultGridData: defaultKSK2140_R01InspectRow,
		selectedRow: selectedKSK2140_R01InspectList,
		originalGridData: originalKSK2140_R01InspectList,
		updateRow: (index: number, data: any) =>
			updateKSK2140_R01InspectList(index, data),
	});

	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK2140_R01InspectDto>({
			idKey: "valueIndex",
			selectedList: selectedKSK2140_R01InspectList,
			deletedList: deletedKSK2140_R01InspectList,
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedKSK2140_R01InspectList,
		deletedKSK2140_R01InspectList,
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	const defaultEditableColumns = ["badCase", "badQty"];
	const handleGridDropDownCommitAfter = useMemo(
		() => createGridDropDownCommitAfter<KSK2140_R01InspectDto>(defaultEditableColumns),
		[],
	);

	const baseColumnDefs = useMemo<ColDef<KSK2140_R01InspectDto>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: renderDescText(DESC_FLD.DEFECT_REASON, "불량사유"), colId: "badCase", field: "badCase", width: 130,
			...CommonCodeColumn.options(
				faultyValueList,
				(params) => isInspectRowEditable(params.data),
				CommonCodeColumn.change(updateKSK2140_R01InspectList, {
					canChange: ({ data }) => isInspectRowEditable(data),
				}),
				handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"), colId: "badQty", field: "badQty", width: 130,
			editable: (params) => isInspectRowEditable(params.data),
			pasteable: true,
		},
	], [faultyValueList, updateKSK2140_R01InspectList, handleGridDropDownCommitAfter, renderDescText]);

	const columnDefs = useMemo<ColDef<KSK2140_R01InspectDto>[]>(() => {
		if (inspectColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<KSK2140_R01InspectDto>({
			baseColumnDefs,
			columnState: inspectColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, inspectColumnState, headerNames]);

	const handleCellChange = useCellChangeHandler({
		dataList: inspectRows,
		originalDataList: originalKSK2140_R01InspectList,
		updateRow: updateKSK2140_R01InspectList,
		customBeforeUpdate: (updatedData, field) => {
			if (!updatedData.inserted) {
				const original = originalKSK2140_R01InspectList.find(
					(item) => item.valueIndex === updatedData.valueIndex,
				);
				if (original) {
					return {
						...updatedData,
						[field]: original[field as keyof KSK2140_R01InspectDto],
					};
				}
			}
			return updatedData;
		},
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: inspectRows,
		idKey: "valueIndex",
		selectedList: selectedKSK2140_R01InspectList,
		deletedList: deletedKSK2140_R01InspectList,
		addSelected: addSelectedKSK2140_R01InspectList,
		removeSelected: removeSelectedKSK2140_R01InspectList,
		clickable,
		setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: inspectRows as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const cellClickActions: CustomCellClickAction<KSK2140_R01InspectDto>[] = useMemo(() => [
		{
			validate: (field, data) =>
				isInspectRowEditable(data) && defaultEditableColumns.includes(field),
			action: (e) => {
				e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
			},
		},
	], []);
	const handleCellClick = CreateCellClickHandler(inspectRows, "valueIndex", cellClickActions);

	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2140_R01InspectDto>[] = useMemo(() => [], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(
		inspectRows,
		"valueIndex",
		cellDoubleClickActions,
	);

	const pinnedBottomRowData = useMemo(() => [
		{
			badCaseNm: formatDescCount(inspectRows.length, countTemplate),
			badQty: inspectRows.reduce(
				(sum, item) =>
					sum +
					(deletedKSK2140_R01InspectList.includes(item.valueIndex.toString())
						? 0
						: Number(item.badQty) || 0),
				0,
			),
		},
	], [countTemplate, deletedKSK2140_R01InspectList, inspectRows]);

	const badQtyTotal = useMemo(
		() => inspectRows.reduce(
			(sum, item) =>
				sum + (deletedKSK2140_R01InspectList.includes(item.valueIndex.toString())
					? 0
					: Number(item.badQty) || 0),
			0,
		),
		[deletedKSK2140_R01InspectList, inspectRows],
	);

	const isEditable = selectedKSK2140_R01List.length > 0 && !!activeInspectKey;

	const handleInsertRow = useCallback(() => {
		if (!isEditable) {
			showWarning("생산실적을 먼저 선택해 주세요.");
			return;
		}
		insertKSK2140_R01InspectList([defaultKSK2140_R01InspectRow]);
	}, [defaultKSK2140_R01InspectRow, insertKSK2140_R01InspectList, isEditable]);

	const handleRemoveRow = useCallback(() => {
		if (inspectRows.length > 1) {
			const lastItem = inspectRows[inspectRows.length - 1];
			removeKSK2140_R01InspectList(lastItem.valueIndex);
			return;
		}
		showWarning("마지막 행은 삭제할 수 없습니다.");
	}, [inspectRows, removeKSK2140_R01InspectList]);

	const handleConfirmClick = useCallback(() => {
		if (!activeInspectKey) {
			showWarning("검사할 상세 행을 선택해 주세요.");
			return;
		}

		const targetDetail = KSK2140_R01DetailList.find(
			(item) => getKSK2140_R01InspectKey(item) === activeInspectKey,
		);
		if (!targetDetail) {
			showWarning("해당하는 상세 행을 찾을 수 없습니다.");
			return;
		}

		const inspectQty = Number(inspectQtyInput) || 0;
		if (inspectQty <= 0) {
			showWarning("검사량은 0보다 커야 합니다.");
			return;
		}

		const remainingQty = Number(targetDetail.lotQty || 0);
		if (inspectQty > remainingQty) {
			showWarning("검사량은 미검사량보다 클 수 없습니다.");
			return;
		}

		if (badQtyTotal > inspectQty) {
			showWarning("불량수량은 검사량보다 클 수 없습니다.");
			return;
		}
		const acceptQty = inspectQty - badQtyTotal;

		const nextDetailList = KSK2140_R01DetailList.map((item) =>
			getKSK2140_R01InspectKey(item) === activeInspectKey
				? {
					...item,
					inspectQty,
					badQty: badQtyTotal,
					acceptQty,
					checkFinish: "Y" as const,
					isModified: true,
				}
				: item,
		);

		updateKSK2140_R01DetailList(nextDetailList);
		setKSK2140_R01LotList(getLotListByInspectFinish(nextDetailList));
	}, [
		KSK2140_R01DetailList,
		activeInspectKey,
		badQtyTotal,
		inspectQtyInput,
		setKSK2140_R01LotList,
		updateKSK2140_R01DetailList,
	]);

	const handleResetClick = useCallback(() => {
		if (!activeInspectKey) {
			showWarning("초기화할 상세 행을 선택해 주세요.");
			return;
		}

		const targetDetail = KSK2140_R01DetailList.find(
			(item) => getKSK2140_R01InspectKey(item) === activeInspectKey,
		);
		if (!targetDetail) {
			showWarning("해당하는 상세 행을 찾을 수 없습니다.");
			return;
		}

		setKSK2140_R01InspectList([{
			...defaultKSK2140_R01InspectRow,
			valueIndex: 1,
			orderIndex: 1,
			orderIndexSeq: 0,
			inserted: true,
			basOrderNo: targetDetail.basOrderNo || "",
			lotNo: targetDetail.lotNo || targetDetail.basLotNo || "",
			basLotNo: targetDetail.basLotNo || targetDetail.lotNo || "",
			itemCd: targetDetail.itemCd || "",
		}]);
		setInspectQtyInput(Number(targetDetail.lotQty || 0));
		initSelectedKSK2140_R01InspectList();
	}, [
		KSK2140_R01DetailList,
		activeInspectKey,
		defaultKSK2140_R01InspectRow,
		initSelectedKSK2140_R01InspectList,
		setKSK2140_R01InspectList,
		setInspectQtyInput,
	]);

	const handleInspectQtyChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setInspectQtyInput(Number(e.target.value) || 0);
	}, [setInspectQtyInput]);

	return (
		<div className="h-full min-h-0 flex flex-col gap-[4px]">
			<div className="w-full shrink-0 flex items-center justify-between gap-[8px]">
				<div className="flex items-center gap-[8px]">
					<button
						className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
						onClick={handleInsertRow}
						disabled={!isEditable}
					>
						{renderDescText(DESC_FLD.ROW_ADD, "행+")}
					</button>
					<button
						className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
						onClick={handleRemoveRow}
						disabled={!isEditable}
					>
						{renderDescText(DESC_FLD.ROW_DELETE, "행-")}
					</button>
				</div>
				<div className="flex items-center gap-[8px]">
					<KioskWorkStandardButtonForm
						buttonStyle="h-[30px] w-[73px] rounded-[6px]"
						onClick={sheetNo => void handleWorkStandardClick(sheetNo)}
					/>
					<button
						className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-2 rounded shadow h-[30px] w-[60px]`}
						onClick={handleResetClick}
						disabled={!isEditable}
					>
						{renderDescText(DESC_BTN.RESET, "초기화")}
					</button>
				</div>
			</div>
			<div className="w-full min-h-0 flex-1 flex flex-col">
				<GridContextWrapper<KSK2140_R01InspectDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setKSK2140_R01InspectList}
					initSelected={initSelectedKSK2140_R01InspectList}
					defaultRowData={defaultKSK2140_R01InspectRow}
					baseColumnDefs={columnDefs}
					setColumnState={setInspectColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<KSK2140_R01InspectDto>
						ref={gridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						overlayNoRowsTemplate={noDataOverlay}
						defaultColDef={gridDefaultColDef}
						rowClassRules={createRowClassRules<KSK2140_R01InspectDto>({
							idKey: "valueIndex",
							selectedList: selectedKSK2140_R01InspectList,
							deletedList: deletedKSK2140_R01InspectList,
						})}
						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={setInspectColumnState}
					/>
				</GridContextWrapper>
			</div>
			<div className="w-full shrink-0 flex items-end justify-start gap-[8px]">
				<div className="flex flex-col gap-[4px]">
					<InputForm
						title={renderDescText(DESC_FLD.INSPECTION_QTY, "검사량")}
						titleWidth={formTitleWidth}
						value={inspectQtyInput || 0}
						editable={true}
						name="inspectQty"
						onChange={handleInspectQtyChange}
						wrapperOption={{
							wrapperStyle: "w-[230px] min-w-[230px] h-[29px]",
							titleStyle: "w-[100px] min-w-[100px] text-left",
							inputStyle: "w-[130px] min-w-[130px]",
						}}
					/>
					<InputForm
						title={renderDescText(DESC_FLD.DEFECT_QTY, "불량수량")}
						titleWidth={formTitleWidth}
						value={badQtyTotal}
						editable={false}
						name="badQty"
						wrapperOption={{
							wrapperStyle: "w-[230px] min-w-[230px] h-[29px]",
							titleStyle: "w-[100px] min-w-[100px] text-left",
							inputStyle: "w-[130px] min-w-[130px]",
						}}
					/>
				</div>
				<button
					className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[32px]`}
					onClick={handleConfirmClick}
					disabled={!isEditable}
				>
					{renderDescText(DESC_BTN.CONFIRM, "확인")}
				</button>
			</div>
		</div>
	);
};
